// src/components/Nav.jsx
import React, { useState, useEffect } from 'react';
import { FaMicrophone } from 'react-icons/fa6';
import { RxHamburgerMenu } from 'react-icons/rx';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Calendar, ScrollText, Calculator, Users, MapPin, LineChart, UserCheck, Clock,
  FileText, Edit2, Wand2, BarChart3, Speaker, Image, Play, Share2, Video,
  UserCog, Music, Camera, Lightbulb, Palette, Building2, Users2, Target, Film,
  Building, Megaphone, Store, Globe
} from 'lucide-react';
import styles from '../styles/nav.module.css';
import Dictaphone from './speechRecognition';
import logo from '../assets/img/logo1.png';
import { RiHome2Fill } from 'react-icons/ri';
import { MdLanguage } from 'react-icons/md';
import { FaRegCircleUser } from 'react-icons/fa6';
import { IoIosSearch, IoIosArrowDown } from 'react-icons/io';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { useI18n } from '../i18n.js';
import { baseUrl } from '../config';
// import LanguageSwitcher from './LanguageSwitcher'; // ✅ ADD THIS IMPORT

export default function Nav() {
  const [loggedIn, setLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [arrowDown, setArrowDown] = useState(false);
  const [indArrowDown, setIndArrowDown] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  // Use i18n context
  const { t, locale, changeLanguage } = useI18n();

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    { code: 'zh', name: '中文' },
    { code: 'ja', name: '日本語' },
    { code: 'ru', name: 'Русский' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'ta', name: 'தமிழ்' },
    { code: 'bn', name: 'বাংলা' },
  ];

  const toggleLangMenu = () => setLangMenuOpen(!langMenuOpen);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setLangMenuOpen(false);
  };

  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const response = await fetch(`${baseUrl}/getUserIDByToken`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        setLoggedIn(response.ok);
      } catch {
        setLoggedIn(false);
      }
    };
    checkLoginStatus();
  }, []);

  const logOut = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_dashboard');
    setLoggedIn(false);
    navigate('/');
  };

  const toggleSearchBar = () => setSearchOpen(prev => !prev);
  const toggleArrow = () => setArrowDown(prev => !prev);
  const toggleIndArrow = () => setIndArrowDown(prev => !prev);

  // Function to get dashboard route based on user type (matching backend logic)
  const getDashboardRoute = (userType) => {
    if (userType === 'marketing') {
      return '/marketing-agencies';
    } else if (userType === 'enthusiast') {
      return '/film-enthusiast';
    } else if (userType === 'lead') {
      return '/production-house';
    } else {
      return '/dashboard';
    }
  };

  // Navigate to dashboard - fetch user details on-demand
  const navigateToDashboard = async () => {
    try {
      // First check if we have cached dashboard route
      const cachedDashboard = localStorage.getItem('user_dashboard');
      if (cachedDashboard) {
        navigate(cachedDashboard);
        return;
      }

      // Fetch user ID
      const tokenResponse = await fetch(`${baseUrl}/getUserIDByToken`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });

      if (!tokenResponse.ok) {
        navigate('/login');
        return;
      }

      const userData = await tokenResponse.json();

      // Fetch user details
      const userDetailsResponse = await fetch(`${baseUrl}/users/${userData.user_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });

      if (userDetailsResponse.ok) {
        const userDetails = await userDetailsResponse.json();
        
        // Get dashboard route based on user type (same logic as backend)
        const dashboardRoute = getDashboardRoute(userDetails.user_type);
        
        // Cache it for next time
        localStorage.setItem('user_dashboard', dashboardRoute);
        
        // Navigate
        navigate(dashboardRoute);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      navigate('/dashboard');
    }
  };

  const addGlitterEffect = (e) => {
    // Create 10 glitter particles
    for (let i = 0; i < 10; i++) {
      const glitter = document.createElement('div');
      glitter.className = 'glitter';

      // Position glitter relative to the button
      const x = e.clientX - e.target.getBoundingClientRect().left;
      const y = e.clientY - e.target.getBoundingClientRect().top;

      // Random position around the click point
      const randomX = x + (Math.random() - 0.5) * 40;
      const randomY = y + (Math.random() - 0.5) * 40;

      glitter.style.left = `${randomX}px`;
      glitter.style.top = `${randomY}px`;

      // Add to button
      e.target.appendChild(glitter);

      // Remove after animation
      setTimeout(() => {
        if (glitter.parentNode === e.target) {
          e.target.removeChild(glitter);
        }
      }, 800);
    }
  };

  return (
    <nav id={styles.mainNav}>
      <div id={styles.upperNav}>
        <NavLink to="/">
          <img src={logo} alt="Producers Buddy Logo" className={styles.logo} />
        </NavLink>
        {searchOpen && <Dictaphone id={styles.subNavMic} />}
        <div id={styles.loginButton}>
          <div id={styles.navIcons}>
            <IoIosSearch id={styles.searchIcon} onClick={toggleSearchBar} />
            {/* <LanguageSwitcher /> ✅ REPLACED: Old language selector with new component */}
            {location.pathname !== '/' && (
              <button id={styles.homeIcon} onClick={() => navigate('/')}>
                <RiHome2Fill />
              </button>
            )}
            {loggedIn && (
              <FaRegCircleUser 
                id={styles.userIcon} 
                onClick={navigateToDashboard}
                style={{ cursor: 'pointer' }}
                title="Go to Dashboard"
              />
            )}
          </div>
          {!loggedIn ? (
            <>
              {location.pathname !== '/login' && (
                <NavLink to="/login">
                  <input type="button" value={t('nav.login', 'Log in')} id={styles.logIn} className={styles.buttons} />
                </NavLink>
              )}
              {location.pathname !== '/signup' && (
                <NavLink to="/signup">
                  <input
                    type="button"
                    value={t('nav.signup', 'Sign up for free')}
                    id={styles.signUp}
                    className={styles.buttons}
                    onClick={addGlitterEffect}
                  />
                </NavLink>
              )}
            </>
          ) : (
            <input
              type="button"
              value={t('nav.logout', 'Log Out')}
              id={styles.signUp}
              className={styles.buttons}
              onClick={(e) => {
                addGlitterEffect(e);
                logOut();
              }}
            />
          )}
        </div>
      </div>
      <div id={styles.lowerNav}>
        <div id={styles.smallNav}>
          <details>
            <summary>
              <RxHamburgerMenu id={styles.hamburger} />
            </summary>
            <div>
              <div id={styles.smallSearch}>
                <input type="search" placeholder={t('nav.search', 'Search')} id={styles.smallSearchBar} />
                <FaMicrophone id={styles.smallMic} />
              </div>
              <NavLink to="/services">
                <p className={styles.navLink}>{t('nav.services', 'Services')}</p>
              </NavLink>
              <NavLink to="/news">
                <p className={styles.navLink}>{t('nav.news', 'News')}</p>
              </NavLink>
              <details>
                <summary className={styles.navLink} onClick={toggleArrow}>
                  {t('nav.products', 'Products')}
                  {arrowDown ? <IoIosArrowDown id={styles.productsDownArrow} /> : <MdKeyboardArrowRight id={styles.productsRightArrow} />}
                </summary>
                <div id={styles.smallProductOptions}>
                  <ul>
                    <NavLink to="/pre-production"><p>{t('nav.preProduction', 'Pre-Production')}</p></NavLink>
                    <NavLink to="/SAHowTo"><li><ScrollText size={16} /> {t('nav.scriptAnalysis', 'Script Analysis')}</li></NavLink>
                    <NavLink to="/budgetHowTo"><li><Calculator size={16} /> {t('nav.budgeting', 'Budgeting')}</li></NavLink>
                    <NavLink to="/castHowTo"><li><Users size={16} /> {t('nav.castingAssistant', 'Casting Assistant')}</li></NavLink>
                    <NavLink to="/locationHowTo"><li><MapPin size={16} /> {t('nav.location', 'Location')}</li></NavLink>
                  </ul>
                  <ul>
                    <NavLink to="/production-advertising"><p>{t('nav.production', 'Production')}</p></NavLink>
                    <p>Coming Soon</p>
                  </ul>
                  <ul>
                    <NavLink to="/post-production"><p>{t('nav.postProduction', 'Post-Production')}</p></NavLink>
                    <p>Coming Soon</p>
                  </ul>
                  {/*
                      <ul>
                        <NavLink to="/production"><p>{t('nav.production', 'Production')}</p></NavLink>
                        <NavLink to="/tracker"><li><LineChart size={16} /> {t('nav.progressManagement', 'Progress Management')}</li></NavLink>
                        <NavLink to="/cast-management"><li><UserCheck size={16} /> {t('nav.castManagement', 'Cast Management')}</li></NavLink>
                        <NavLink to="/schedule"><li><Clock size={16} /> {t('nav.schedulingAssistant', 'Scheduling Assistant')}</li></NavLink>
                        <NavLink to="/documents"><li><FileText size={16} /> {t('nav.documentation', 'Documentation')}</li></NavLink>
                      </ul>
                      <ul>
                        <NavLink to="/post-production"><p>{t('nav.postProduction', 'Post-Production')}</p></NavLink>
                        <NavLink to="/editing"><li><Edit2 size={16} /> {t('nav.editing', 'Editing')}</li></NavLink>
                        <NavLink to="/vfx"><li><Wand2 size={16} /> {t('nav.visualEffects', 'Visual Effects')}</li></NavLink>
                        <NavLink to="/post-analysis"><li><BarChart3 size={16} /> {t('nav.marketingAnalyst', 'Marketing Analyst')}</li></NavLink>
                        <NavLink to="/advertisement-promotions"><li><Speaker size={16} /> {t('nav.advertisements', 'Advertisements')}</li></NavLink>
                        <NavLink to="/postergenerationpage"><li><Image size={16} /> {t('nav.posters', 'Posters')}</li></NavLink>
                        <NavLink to="/trailer"><li><Play size={16} /> {t('nav.trailers', 'Trailers')}</li></NavLink>
                        <NavLink to="/distributions"><li><Share2 size={16} /> {t('nav.distributions', 'Distributions')}</li></NavLink>
                      </ul>
                      */}
                </div>
              </details>
              {/*
                  <details>
                    <summary className={styles.navLink} onClick={toggleIndArrow}>
                      {t('nav.industries', 'Industries')}
                      {indArrowDown ? <IoIosArrowDown id={styles.industriesDownArrow} /> : <MdKeyboardArrowRight id={styles.industriesRightArrow} />}
                    </summary>
                    <div id={styles.industriesOptions}>
                      <ul>
                        <NavLink to="/film-enthusiast-industry"><p>{t('nav.filmEnthusiast', 'Film Enthusiast')}</p></NavLink>
                        <NavLink to="/actor"><li><Film size={16} /> <span>{t('nav.actor', 'Actor/Actress')}</span></li></NavLink>
                        <NavLink to="/director"><li><Video size={16} /> <span>{t('nav.director', 'Director')}</span></li></NavLink>
                        <NavLink to="/cinematographer"><li><Camera size={16} /> <span>{t('nav.cinematographer', 'Cinematographer')}</span></li></NavLink>
                        <NavLink to="/sound-artist"><li><Music size={16} /> <span>{t('nav.soundArtist', 'Sound Artist')}</span></li></NavLink>
                        <NavLink to="/lighting"><li><Lightbulb size={16} /> <span>{t('nav.lightingArtist', 'Lighting Artist')}</span></li></NavLink>
                        <NavLink to="/designer"><li><Palette size={16} /> <span>{t('nav.designer', 'Designer')}</span></li></NavLink>
                      </ul>
                      <ul>
                        <NavLink to="/production-house-industry"><p>{t('nav.productionHouse', 'Production House')}</p></NavLink>
                        <NavLink to="/production-manager"><li><UserCog size={16} /> <span>{t('nav.productionManager', 'Production Manager')}</span></li></NavLink>
                        <NavLink to="/executive-producer"><li><Building2 size={16} /> <span>{t('nav.executiveProducer', 'Executive Producer')}</span></li></NavLink>
                      </ul>
                      ** Add Marketing Enthusiast **
                    </div>
                  </details>
                  */}
              <NavLink to="/services">Pricing</NavLink>
            </div>
          </details>
        </div>
        <div id={styles.bigNav}>
          <details>
            <summary className={styles.navLink} onClick={toggleArrow}>
              {t('nav.products', 'Products')}
              {arrowDown ? <IoIosArrowDown id={styles.productsDownArrow} /> : <MdKeyboardArrowRight id={styles.productsRightArrow} />}
            </summary>
            <div id={styles.productOptions}>
              <ul>
                <NavLink to="/pre-production"><p>{t('nav.preProduction', 'Pre-Production')}</p></NavLink>
                <NavLink to="/SAHowTo"><li><ScrollText size={16} /> {t('nav.scriptAnalysis', 'Script Analysis')}</li></NavLink>
                <NavLink to="/budgetHowTo"><li><Calculator size={16} /> {t('nav.budgeting', 'Budgeting')}</li></NavLink>
                <NavLink to="/castHowTo"><li><Users size={16} /> {t('nav.castingAssistant', 'Casting Assistant')}</li></NavLink>
                <NavLink to="/locationHowTo"><li><MapPin size={16} /> {t('nav.location', 'Location')}</li></NavLink>
              </ul>
              <ul>
                <NavLink to="/production-advertising"><p>{t('nav.production', 'Production')}</p></NavLink>
                <p>Coming Soon</p>
              </ul>
              <ul>
                <NavLink to="/post-production"><p>{t('nav.postProduction', 'Post-Production')}</p></NavLink>
                <p id={styles}>Coming Soon</p>
              </ul>
              {/*
                  <ul>
                    <NavLink to="/production"><p>{t('nav.production', 'Production')}</p></NavLink>
                    <NavLink to="/tracker"><li><LineChart size={16} /> {t('nav.progressManagement', 'Progress Management')}</li></NavLink>
                    <NavLink to="/cast-management"><li><UserCheck size={16} /> {t('nav.castManagement', 'Cast Management')}</li></NavLink>
                    <NavLink to="/schedule"><li><Clock size={16} /> {t('nav.schedulingAssistant', 'Scheduling Assistant')}</li></NavLink>
                    <NavLink to="/documents"><li><FileText size={16} /> {t('nav.documentation', 'Documentation')}</li></NavLink>
                  </ul>
                  <ul>
                    <NavLink to="/post-production"><p>{t('nav.postProduction', 'Post-Production')}</p></NavLink>
                    <NavLink to="/editing"><li><Edit2 size={16} /> {t('nav.editing', 'Editing')}</li></NavLink>
                    <NavLink to="/vfx"><li><Wand2 size={16} /> {t('nav.visualEffects', 'Visual Effects')}</li></NavLink>
                    <NavLink to="/post-analysis"><li><BarChart3 size={16} /> {t('nav.marketingAnalyst', 'Marketing Analyst')}</li></NavLink>
                    <NavLink to="/advertisement-promotions"><li><Speaker size={16} /> {t('nav.advertisements', 'Advertisements')}</li></NavLink>
                    <NavLink to="/postergenerationpage"><li><Image size={16} /> {t('nav.posters', 'Posters')}</li></NavLink>
                    <NavLink to="/trailer"><li><Play size={16} /> {t('nav.trailers', 'Trailers')}</li></NavLink>
                    <NavLink to="/distributions"><li><Share2 size={16} /> {t('nav.distributions', 'Distributions')}</li></NavLink>
                  </ul>
                  */}
            </div>
          </details>
          <NavLink to="/blog">
            <p className={styles.navLink}>{t('nav.postYourThoughts', 'Post your thoughts')}</p>
          </NavLink>
          <NavLink to="/news">
            <p className={styles.navLink}>{t('nav.news', 'News')}</p>
          </NavLink>
          <NavLink to="/AboutUs">
            <p className={styles.navLink}>{t('nav.whoWeAre', 'Who we are')}</p>
          </NavLink>
          <NavLink to="/contactus">
            <p className={styles.navLink}>{t('nav.contactUs', 'Contact Us')}</p>
          </NavLink>
          {/*
              <details>
                <summary className={styles.navLink} onClick={toggleIndArrow}>
                  {t('nav.industries', 'Industries')}
                  {indArrowDown ? <IoIosArrowDown id={styles.industriesDownArrow} /> : <MdKeyboardArrowRight id={styles.industriesRightArrow} />}
                </summary>
                <div id={styles.industriesOptions}>
                  <ul>
                    <NavLink to="/film-enthusiast-industry"><p>{t('nav.filmEnthusiast', 'Film Enthusiast')}</p></NavLink>
                    <NavLink to="/actor"><li><Film size={16} /> <span>{t('nav.actor', 'Actor/Actress')}</span></li></NavLink>
                    <NavLink to="/director"><li><Video size={16} /> <span>{t('nav.director', 'Director')}</span></li></NavLink>
                    <NavLink to="/cinematographer"><li><Camera size={16} /> <span>{t('nav.cinematographer', 'Cinematographer')}</span></li></NavLink>
                    <NavLink to="/sound-artist"><li><Music size={16} /> <span>{t('nav.soundArtist', 'Sound Artist')}</span></li></NavLink>
                    <NavLink to="/lighting"><li><Lightbulb size={16} /> <span>{t('nav.lightingArtist', 'Lighting Artist')}</span></li></NavLink>
                    <NavLink to="/designer"><li><Palette size={16} /> <span>{t('nav.designer', 'Designer')}</span></li></NavLink>
                  </ul>
                  <ul>
                    <NavLink to="/production-house-industry"><p>{t('nav.productionHouse', 'Production House')}</p></NavLink>
                    <NavLink to="/production-manager"><li><UserCog size={16} /> <span>{t('nav.productionManager', 'Production Manager')}</span></li></NavLink>
                    <NavLink to="/executive-producer"><li><Building2 size={16} /> <span>{t('nav.executiveProducer', 'Executive Producer')}</span></li></NavLink>
                  </ul>
                  ** Add Marketing Enthusiast **
                </div>
              </details>
              */}
          <NavLink to="/services"><p className={styles.navLink}>Pricing</p></NavLink>
        </div>
      </div>
    </nav>
  );
}
